import { useMemo, useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Board } from "@/components/board/Board";
import { TESTIDS } from "@/constants/testIds";
import {
  applyMove,
  countersOnBoardOf,
  createInitialState,
  decodePosition,
  encodePosition,
  getLegalMoves,
  totalCounters,
  undoMove,
} from "@/game/engine";
import { toast } from "sonner";
import { Copy, RotateCcw, Upload, Undo2 } from "lucide-react";
import { EventLog } from "@/components/game/EventLog";
import { Badge } from "@/components/ui/badge";

export function Practice() {
  const [state, setState] = useState(() => createInitialState());
  const [code, setCode] = useState("");
  const legalSet = useMemo(
    () => new Set(getLegalMoves(state).map(([r, c]) => `${r},${c}`)),
    [state]
  );

  const onPitSelect = (r, c) => {
    try {
      const { state: next } = applyMove(state, r, c);
      setState(next);
    } catch {
      toast.error("Illegal move in current state.");
    }
  };

  const togglePlayer = () => {
    setState({ ...state, currentPlayer: state.currentPlayer === "A" ? "B" : "A" });
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(encodePosition(state));
      toast.success("Position code copied to clipboard.");
    } catch {
      toast.error("Could not access clipboard.");
    }
  };

  const loadCode = () => {
    try {
      const { board, currentPlayer } = decodePosition(code.trim());
      const total = board.flat().reduce((a, b) => a + b, 0);
      if (total < 0 || total > 200) throw new Error("UNREASONABLE_TOTAL");
      setState({ ...createInitialState(), board, currentPlayer });
      toast.success("Loaded position.");
    } catch (e) {
      toast.error("Invalid position code.");
    }
  };

  return (
    <Layout>
      <section
        data-testid={TESTIDS.practicePage}
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-12 gap-6"
      >
        <div className="lg:col-span-8 space-y-4">
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Practice</p>
              <h1 className="font-display text-3xl sm:text-4xl mt-1">Sandbox board</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Test moves freely. Statistics aren&apos;t recorded here.
              </p>
            </div>
            <Badge variant="outline" className="rounded-full">
              {state.currentPlayer === "A" ? "Player 1 to play" : "Player 2 to play"}
            </Badge>
          </div>

          <Board
            board={state.board}
            currentPlayer={state.currentPlayer}
            legalSet={legalSet}
            onPitSelect={onPitSelect}
            showHints
            coords
            disabled={false}
            sowingDirection={state.sowingDirection || "CCW"}
          />

          <div className="flex flex-wrap gap-2">
            <Button
              data-testid={TESTIDS.practiceTogglePlayer}
              variant="outline"
              onClick={togglePlayer}
              className="rounded-full"
            >
              Swap active player
            </Button>
            <Button
              variant="outline"
              onClick={() => setState(undoMove(state))}
              disabled={state.history.length === 0}
              className="rounded-full"
            >
              <Undo2 className="h-4 w-4 mr-2" /> Undo
            </Button>
            <Button
              data-testid={TESTIDS.practiceResetBtn}
              variant="outline"
              onClick={() => setState(createInitialState())}
              className="rounded-full"
            >
              <RotateCcw className="h-4 w-4 mr-2" /> Reset
            </Button>
            <Button
              data-testid={TESTIDS.practiceSaveBtn}
              onClick={copyCode}
              className="rounded-full"
            >
              <Copy className="h-4 w-4 mr-2" /> Copy position
            </Button>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <Card className="p-4 space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Position code</p>
            <Input
              data-testid={TESTIDS.practiceCodeInput}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="22222222-00000000-00000000-22222222:A"
              className="font-mono text-xs"
            />
            <div className="flex gap-2">
              <Button
                data-testid={TESTIDS.practiceLoadBtn}
                size="sm"
                onClick={loadCode}
                className="rounded-full"
              >
                <Upload className="h-4 w-4 mr-2" /> Load
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setCode(encodePosition(state))}
                className="rounded-full"
              >
                Use current
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Counters on board: <span className="font-mono">{totalCounters(state) - state.captures.A - state.captures.B}</span>
              · A: <span className="font-mono">{countersOnBoardOf(state.board, "A")}</span>
              · B: <span className="font-mono">{countersOnBoardOf(state.board, "B")}</span>
            </p>
          </Card>

          <EventLog state={state} />
        </aside>
      </section>
    </Layout>
  );
}
